import {
  Component,
  For,
  Show,
  JSX,
  createSignal,
  createEffect,
  onCleanup,
} from "solid-js";

export interface MultiSelectOption {
  value: string;
  label: string;
  icon?: JSX.Element;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  placeholder?: string;
  disabled?: boolean;
  class?: string;
  onChange: (selected: string[]) => void;
}

export const MultiSelect: Component<MultiSelectProps> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [highlightedIndex, setHighlightedIndex] = createSignal(0);
  let containerRef: HTMLDivElement | undefined;

  const hasSelection = () => props.selected.length > 0;

  const displayText = () => {
    if (props.selected.length === 0) return props.placeholder ?? "Select...";
    if (props.selected.length === 1) {
      const opt = props.options.find((o) => o.value === props.selected[0]);
      return opt?.label ?? "1 selected";
    }
    return `${props.selected.length} selected`;
  };

  const toggleOption = (value: string) => {
    const next = props.selected.includes(value)
      ? props.selected.filter((v) => v !== value)
      : [...props.selected, value];
    props.onChange(next);
  };

  createEffect(() => {
    if (!isOpen()) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef && !containerRef.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    onCleanup(() => document.removeEventListener("mousedown", handleMouseDown));
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (props.disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen()) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((i) => Math.min(i + 1, props.options.length - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen() && props.options[highlightedIndex()]) {
          toggleOption(props.options[highlightedIndex()].value);
        } else {
          setIsOpen(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      case "Home":
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHighlightedIndex(props.options.length - 1);
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      class={"relative " + (props.class ?? "")}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        class={
          "w-full flex items-center justify-between rounded border h-7 px-3 py-0 text-sm text-left transition-colors focus-ring " +
          (props.disabled
            ? "opacity-50 cursor-not-allowed bg-sunken border-line"
            : hasSelection()
              ? "bg-sunken border-line-strong cursor-pointer"
              : "bg-surface border-line cursor-pointer")
        }
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen()}
        aria-label={
          props.placeholder
            ? `${props.placeholder} filter${hasSelection() ? `, ${props.selected.length} selected` : ""}`
            : undefined
        }
        onClick={() => !props.disabled && setIsOpen(!isOpen())}
      >
        <span class={hasSelection() ? "text-ink" : "text-ink-3"}>
          {displayText()}
        </span>
        <svg
          class={
            "w-4 h-4 text-ink-3 transition-transform ml-2 flex-shrink-0 " +
            (isOpen() ? "rotate-180" : "")
          }
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <Show when={isOpen()}>
        <div
          class="absolute z-20 w-full mt-1 min-w-[200px] bg-surface border border-line rounded shadow-overlay py-1 animate-fade-in"
          role="listbox"
          aria-multiselectable="true"
        >
          <ul class="max-h-60 overflow-auto">
            <For each={props.options}>
              {(option, index) => {
                const isSelected = () => props.selected.includes(option.value);
                return (
                  <li
                    role="option"
                    aria-selected={isSelected()}
                    class={
                      "px-3 py-2 text-sm cursor-pointer " +
                      "flex items-center gap-2.5 transition-colors " +
                      (index() === highlightedIndex()
                        ? "bg-sunken"
                        : "hover:bg-paper")
                    }
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggleOption(option.value)}
                    onMouseEnter={() => setHighlightedIndex(index())}
                  >
                    <input
                      type="checkbox"
                      class="w-4 h-4 rounded-sm border-line accent-ink cursor-pointer"
                      checked={isSelected()}
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                    <Show when={option.icon}>
                      <span class="flex-shrink-0">{option.icon}</span>
                    </Show>
                    <span class={isSelected() ? "text-ink" : "text-ink-2"}>
                      {option.label}
                    </span>
                  </li>
                );
              }}
            </For>
          </ul>
        </div>
      </Show>
    </div>
  );
};
